// Manages superuser mode countdown, interaction-based extension, and broadcast-driven tab detection
import { Controller } from "@hotwired/stimulus"
import CSRFFetch from "@/javascript/components/tools/fetch"
import { createLogger } from "@/javascript/utils/logging"

const logger = createLogger('SuperuserTimeoutController')

const DEBOUNCE_SECONDS = 3
const INTERACTION_EVENTS = ["click", "keydown", "scroll"]

export default class extends Controller {
  static values = { expiresAt: Number, url: String, deactivateUrl: String }
  static targets = ["countdown", "banner", "passiveModal", "expirySignal", "activationSignal"]

  connect() {
    this.lastExtendedAt = 0
    this.boundExtend = this.extendTimeout.bind(this)
    INTERACTION_EVENTS.forEach(e => document.addEventListener(e, this.boundExtend))
    this.startCountdown()
  }

  disconnect() {
    INTERACTION_EVENTS.forEach(e => document.removeEventListener(e, this.boundExtend))
    this.stopCountdown()
  }

  expiresAtValueChanged() {
    this.startCountdown()
  }

  activationSignalTargetConnected(element) {
    element.remove()
    if (!sessionStorage.getItem('superuser_activated_here')) {
      this.stopCountdown()
      INTERACTION_EVENTS.forEach(e => document.removeEventListener(e, this.boundExtend))
      this.bannerTarget.remove()
      if (this.hasPassiveModalTarget) this.passiveModalTarget.showModal()
    }
  }

  expirySignalTargetConnected(element) {
    this.expiresAtValue = parseInt(element.dataset.expiresAt, 10)
    element.remove()
  }

  startCountdown() {
    this.stopCountdown()
    if (!this.hasCountdownTarget) return

    this.updateCountdown()
    this.timer = setInterval(() => this.updateCountdown(), 1000)
  }

  stopCountdown() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  updateCountdown() {
    const remaining = this.expiresAtValue - Math.floor(Date.now() / 1000)

    if (remaining <= 0) {
      this.stopCountdown()
      this.countdownTarget.textContent = '(0:00)'
      return
    }

    const minutes = Math.floor(remaining / 60)
    const seconds = remaining % 60
    this.countdownTarget.textContent = `(${minutes}:${String(seconds).padStart(2, '0')})`
  }

  extendTimeout(event) {
    if (event?.type === "click" && event.target?.closest?.('.js-admin-banner')) return

    const now = Date.now()
    if (now - this.lastExtendedAt < DEBOUNCE_SECONDS * 1000) return

    this.lastExtendedAt = now
    CSRFFetch(this.urlValue, { method: "POST" })
  }

  useHere() {
    sessionStorage.setItem('superuser_activated_here', 'true')
    window.location.reload()
  }

  deactivateSuperuser() {
    sessionStorage.setItem('superuser_deactivated_here', 'true')
    CSRFFetch(this.deactivateUrlValue, { method: "POST" }).catch((error) => {
      logger.error("Failed to deactivate superuser mode:", error)
      window.location.reload()
    })
  }
}
